import { Formik, Form, Field, FieldArray } from 'formik';
import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Editor from 'react-simple-code-editor';
import useGallery from './hooks/useGallery';
import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism.css';
import useTemplates, {
    EmailTemplate,
} from '../../EmailTemplates/components/hooks/useTemplates';
import CrowdGallery from './CrowdGallery';
import useSurveyQuestions from './hooks/useSurveyQuestions';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Textarea } from '@/src/shadComponents/ui/textarea';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { FeatureFlags } from '@/src/Features/lib/constants';
import usePicTime from '@/src/Orgs/components/hooks/usePicTime';
import EditGalleryV2 from './EditGalleryV2';
import { Loader2 } from 'lucide-react';

interface SurveyQuestion {
    question: string;
    answerTypeId: number;
    id?: number;
    isDeleted?: boolean;
}

interface GalleryMediaFilter {
    enable: boolean;
    original: boolean;
    videos: boolean;
    photos: boolean;
    prints: boolean;
    edited: boolean;
}

interface GalleryObject {
    name: string;
    isPrivate: boolean;
    password: string;
    slug: string;
    id?: number;
    textSettingsId?: number;
    emailSettingsId?: number;
    activationId: number;
    emailBody: string;
    emailEnabled: boolean;
    fromAddress: string;
    fromName: string;
    subject: string;
    textBody: string;
    textEnabled: boolean;
    foregroundColor: string;
    backgroundColor: string;
    qrLogo: string;
    bottomLogo: string;
    logo: string;
    font: string;
    ctaImage: string;
    ctaText: string;
    ctaLink: string;
    headerBackgroundColor: string;
    headerForegroundColor: string;
    sendLinkToPhoto: boolean;
    showGalleryLink: boolean;
    surveyRequired: boolean;
    ageVerification: boolean;
    verifiedAge: number | null;
    surveyQuestions: [SurveyQuestion];
    galleryMediaFilter: GalleryMediaFilter;
    disclaimer: string;
    disclaimerRequired: boolean;
    disclaimerEnabled: boolean;
    disclaimerOptOut: boolean;
    metaPixelId: string;
    syncWithPictime?: boolean;
    createShareDescriptionText: string;
    createShareDescriptionTextSize: number;
    createShareDescriptionTextFont: string;
    surveyUrl: string | null;
    labelSearchPrompt: string | null;
    preShareSurveyUrl: string | null;
    sendAttachments: boolean;
}

export default function EditGallery() {
    return (
        <FeatureFlag
            flag={FeatureFlags.GALLERY_SETTINGS_V2}
            fallback={() => <EditGalleryLegacy />}
        >
            <EditGalleryV2 />
        </FeatureFlag>
    );
}

function EditGalleryLegacy() {
    const navigate = useNavigate();
    const [hasPictimeSettings, setHasPictimeSettings] =
        useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(true);
    const [gallery, setGallery] = useState<GalleryObject | null>(null);
    const { galleryId: id } = useParams();
    const { getGallery, saveGallery } = useGallery();
    const { getSurveyQuestions } = useSurveyQuestions();
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] =
        useState<EmailTemplate | null>(null);
    const { getTemplates } = useTemplates();
    const galleryId = id ? +id : 0;
    const { isLinkedToPictime } = usePicTime();

    useEffect(() => {
        let isMounted = true;
        const getAccounts = async () => {
            try {
                const gallery = await getGallery(galleryId);
                const surveyQuestions = await getSurveyQuestions(
                    gallery.activationId,
                );
                const templates = await getTemplates();
                const hasPictimeSettings = await isLinkedToPictime();

                if (hasPictimeSettings) {
                }

                gallery.surveyQuestions = surveyQuestions;
                setGallery(gallery);
                setTemplates(templates);
                setHasPictimeSettings(hasPictimeSettings);
            } catch (error) {
                console.error(error);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        getAccounts();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!gallery) {
        return <div className="p-4 text-destructive">Gallery loading error, does it exist?</div>;
    }

    return (
        <div className="max-w-3xl mx-auto py-8 px-4">
            <div className="flex items-center gap-3 mb-6">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">Edit Gallery</h1>
                <CrowdGallery galleryId={galleryId} />
                <Link to="ftp" className="text-sm text-primary hover:underline">[ edit ftp ]</Link>
            </div>
            <Formik
                initialValues={gallery}
                onSubmit={async (values, actions) => {
                    const wasSuccessful = await saveGallery(galleryId, values);
                    actions.setSubmitting(false);

                    if (wasSuccessful) {
                        navigate(-1);
                    }
                }}
            >
                {(props) => (
                    <Form
                        className="space-y-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <div className="space-y-2">
                            <Label htmlFor="name">Name</Label>
                            <Field
                                as={Input}
                                id="name"
                                name="name"
                                placeholder="Gallery Name"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="slug">Slug</Label>
                            <Field
                                as={Input}
                                id="slug"
                                name="slug"
                                placeholder="slug"
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="isPrivate"
                                    name="isPrivate"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="isPrivate">Requires password</Label>
                            </div>
                        </div>

                        {props.values.isPrivate ? (
                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <Field
                                    as={Input}
                                    id="password"
                                    name="password"
                                />
                            </div>
                        ) : null}

                        <h2 className="text-lg font-semibold tracking-tight pt-4">Gallery</h2>

                        <div className="space-y-2">
                            <Label htmlFor="backgroundColor">Background</Label>
                            <Field
                                as={Input}
                                id="backgroundColor"
                                name="backgroundColor"
                                placeholder="#FFFFFF"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="foregroundColor">Foreground</Label>
                            <Field
                                as={Input}
                                id="foregroundColor"
                                name="foregroundColor"
                                placeholder="#000000"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="headerBackgroundColor">Header Background</Label>
                            <Field
                                as={Input}
                                id="headerBackgroundColor"
                                name="headerBackgroundColor"
                                placeholder="#000000"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="headerForegroundColor">Header Foreground</Label>
                            <Field
                                as={Input}
                                id="headerForegroundColor"
                                name="headerForegroundColor"
                                placeholder="#FFFFFF"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="logo">Logo URL</Label>
                            <Field
                                as={Input}
                                id="logo"
                                name="logo"
                                placeholder="url to logo"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="ctaImage">CTA Image URL</Label>
                            <Field
                                as={Input}
                                id="ctaImage"
                                name="ctaImage"
                                placeholder="url to cta image"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="ctaLink">CTA Link URL</Label>
                            <Field
                                as={Input}
                                id="ctaLink"
                                name="ctaLink"
                                placeholder="url to cta link"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="ctaText">CTA Text</Label>
                            <Field
                                as={Input}
                                id="ctaText"
                                name="ctaText"
                                placeholder="cta text"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="labelSearchPrompt">Label Search Prompt</Label>
                            <Field
                                as={Input}
                                id="labelSearchPrompt"
                                name="labelSearchPrompt"
                            />
                        </div>

                        {hasPictimeSettings ? (
                            <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                    <Field
                                        id="syncWithPictime"
                                        name="syncWithPictime"
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-border"
                                    />
                                    <Label htmlFor="syncWithPictime">Sync with Pic-Time</Label>
                                </div>
                            </div>
                        ) : null}

                        <FeatureFlag
                            flag={FeatureFlags.CUSTOMIZABLE_SHARING_STATION_V1}
                        >
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="sharingStationArea1ImageUri">
                                        Sharing Left Image
                                    </Label>
                                    <Field
                                        as={Input}
                                        id="sharingStationArea1ImageUri"
                                        name="sharingStationArea1ImageUri"
                                        placeholder="url to image on left side of sharing experience"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="sharingStationArea2ImageUri">
                                        Sharing Right Image
                                    </Label>
                                    <Field
                                        as={Input}
                                        id="sharingStationArea2ImageUri"
                                        name="sharingStationArea2ImageUri"
                                        placeholder="url to image on right side of sharing experience"
                                    />
                                </div>
                            </>
                        </FeatureFlag>
                        {/*
                        <div className="space-y-2">
                            <Label htmlFor="qrLogo">QR Logo</Label>
                            <Field as={Input} id="qrLogo" name="qrLogo" placeholder="" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="bottomLogo">Footer Logo</Label>
                            <Field
                                as={Input}
                                id="bottomLogo"
                                name="bottomLogo"
                                placeholder=""
                            />
                        </div>*/}

                        <h2 className="text-lg font-semibold tracking-tight pt-4">Sharing Experience</h2>

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="sendLinkToPhoto"
                                    name="sendLinkToPhoto"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="sendLinkToPhoto">Link to photo</Label>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="showGalleryLink"
                                    name="showGalleryLink"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="showGalleryLink">Show gallery link</Label>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="textEnabled"
                                    name="textEnabled"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="textEnabled">Text enabled</Label>
                            </div>
                        </div>

                        {props.values.textEnabled ? (
                            <div className="space-y-2">
                                <Label htmlFor="textBody">Text Body</Label>
                                <Field
                                    as={Textarea}
                                    id="textBody"
                                    name="textBody"
                                    rows={4}
                                />
                            </div>
                        ) : null}

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="emailEnabled"
                                    name="emailEnabled"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="emailEnabled">Email enabled</Label>
                            </div>
                        </div>

                        {props.values.emailEnabled ? (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="emailAddress">Email Address</Label>
                                    <Field
                                        as={Input}
                                        id="emailAddress"
                                        name="fromAddress"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="fromName">From Name</Label>
                                    <Field
                                        as={Input}
                                        id="fromName"
                                        name="fromName"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="emailSubject">Email Subject</Label>
                                    <Field
                                        as={Input}
                                        id="subject"
                                        name="subject"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label>Email Body</Label>
                                        <div className="flex items-center gap-2">
                                            <select
                                                className="text-sm border border-border rounded-md px-3 py-1"
                                                onChange={(e) =>
                                                    setSelectedTemplate(
                                                        templates.find(
                                                            (template) =>
                                                                template.id ===
                                                                +e.currentTarget
                                                                    .value,
                                                        ) ?? null,
                                                    )
                                                }
                                            >
                                                <option>Select template</option>
                                                {templates.map((template) => {
                                                    return (
                                                        <option value={template.id} key={template.id}>
                                                            {template.name}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    if (selectedTemplate) {
                                                        props.setFieldValue(
                                                            'emailBody',
                                                            selectedTemplate?.body,
                                                        );
                                                    }
                                                }}
                                            >
                                                Apply Template
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="border border-border rounded-md overflow-hidden">
                                        <Editor
                                            value={props.values.emailBody}
                                            onValueChange={(code) =>
                                                props.setFieldValue(
                                                    'emailBody',
                                                    code,
                                                )
                                            }
                                            highlight={(code) =>
                                                Prism.highlight(
                                                    code,
                                                    Prism.languages.html,
                                                    'html',
                                                )
                                            }
                                            padding={10}
                                            style={{
                                                fontFamily:
                                                    '"Fira code", "Fira Mono", monospace',
                                                fontSize: 14,
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : null}

                        <h2 className="text-lg font-semibold tracking-tight pt-4">Data Collection</h2>

                        <div className="space-y-2">
                            <Label htmlFor="metaPixelId">Meta Pixel ID</Label>
                            <Field
                                as={Input}
                                id="metaPixelId"
                                name="metaPixelId"
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="disclaimerEnabled"
                                    name="disclaimerEnabled"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="disclaimerEnabled">Disclaimer enabled for share</Label>
                            </div>
                        </div>

                        {props.values.disclaimerEnabled ? (
                            <>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Field
                                            id="disclaimerRequired"
                                            name="disclaimerRequired"
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-border"
                                        />
                                        <Label htmlFor="disclaimerRequired">Disclaimer required for share</Label>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Field
                                            id="disclaimerOptOut"
                                            name="disclaimerOptOut"
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-border"
                                        />
                                        <Label htmlFor="disclaimerOptOut">Is Opt Out</Label>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="disclaimer">Disclaimer</Label>
                                    <Field
                                        as={Input}
                                        id="disclaimer"
                                        name="disclaimer"
                                    />
                                </div>
                            </>
                        ) : null}

                        <div className="space-y-2">
                            <Label htmlFor="createShareDescriptionText">Share Description</Label>
                            <Field
                                as={Input}
                                id="createShareDescriptionText"
                                name="createShareDescriptionText"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="createShareDescriptionTextSize">Share Description Font Size</Label>
                            <Field
                                as={Input}
                                id="createShareDescriptionTextSize"
                                name="createShareDescriptionTextSize"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="createShareDescriptionTextFont">Share Description Font</Label>
                            <Field
                                as={Input}
                                id="createShareDescriptionTextSizeFont"
                                name="createShareDescriptionTextSizFont"
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="surveyRequired"
                                    name="surveyRequired"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="surveyRequired">Survey required for share</Label>
                            </div>
                        </div>

                        <FeatureFlag flag={FeatureFlags.SURVEY_URL_V1}>
                            <div className="space-y-2">
                                <Label htmlFor="surveyUrl">Post-Share Survey URL</Label>
                                <Field
                                    as={Input}
                                    id="surveyUrl"
                                    name="surveyUrl"
                                    type="text"
                                    placeholder="Enter survey URL"
                                />
                            </div>
                        </FeatureFlag>

                        <div className="space-y-2">
                            <Label htmlFor="preShareSurveyUrl">Pre-Share Survey URL</Label>
                            <Field
                                as={Input}
                                id="preShareSurveyUrl"
                                name="preShareSurveyUrl"
                                type="text"
                                placeholder="Enter pre-share survey URL"
                            />
                        </div>

                        {props.values.surveyRequired ? (
                            <div>
                                <FieldArray
                                    name="surveyQuestions"
                                    render={(arrayHelpers) => (
                                        <>
                                            <div className="space-y-4">
                                                {props.values.surveyQuestions &&
                                                props.values.surveyQuestions
                                                    .length > 0
                                                    ? props.values.surveyQuestions.map(
                                                          (question, index) => {
                                                              return question.isDeleted ? null : (
                                                                  <div
                                                                      key={`${props.values.surveyQuestions.length}-${index}`}
                                                                      className="border border-border rounded-lg p-4 space-y-3"
                                                                  >
                                                                      <div className="flex items-center gap-2">
                                                                          <Field
                                                                              as={Input}
                                                                              id={`surveyQuestions.${index}.question`}
                                                                              name={`surveyQuestions.${index}.question`}
                                                                          />
                                                                          <Field
                                                                              id={`surveyQuestions.${index}.answerTypeId`}
                                                                              name={`surveyQuestions.${index}.answerTypeId`}
                                                                              as="select"
                                                                              className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                                                                          >
                                                                              <option
                                                                                  value={
                                                                                      2
                                                                                  }
                                                                              >
                                                                                  text
                                                                              </option>
                                                                              <option
                                                                                  value={
                                                                                      1
                                                                                  }
                                                                              >
                                                                                  yes/no
                                                                              </option>
                                                                          </Field>
                                                                          <Button
                                                                              type="button"
                                                                              variant="outline"
                                                                              size="sm"
                                                                              onClick={() =>
                                                                                  arrayHelpers.push(
                                                                                      {
                                                                                          question:
                                                                                              '',
                                                                                          answerTypeId: 1,
                                                                                      },
                                                                                  )
                                                                              }
                                                                          >
                                                                              +
                                                                          </Button>
                                                                          <Button
                                                                              type="button"
                                                                              variant="outline"
                                                                              size="sm"
                                                                              onClick={() =>
                                                                                  arrayHelpers.replace(
                                                                                      index,
                                                                                      {
                                                                                          ...props
                                                                                              .values
                                                                                              .surveyQuestions[
                                                                                              index
                                                                                          ],
                                                                                          isDeleted:
                                                                                              true,
                                                                                      },
                                                                                  )
                                                                              }
                                                                          >
                                                                              -
                                                                          </Button>
                                                                      </div>
                                                                  </div>
                                                              );
                                                          },
                                                      )
                                                    : null}
                                            </div>
                                            {props.values?.surveyQuestions
                                                .length < 1 ? (
                                                <Button
                                                    variant="outline"
                                                    type="button"
                                                    onClick={() =>
                                                        arrayHelpers.push({
                                                            question: '',
                                                            answerTypeId: 1,
                                                        })
                                                    }
                                                >
                                                    Add Question
                                                </Button>
                                            ) : null}
                                        </>
                                    )}
                                />
                            </div>
                        ) : null}

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="ageVerification"
                                    name="ageVerification"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="ageVerification">Age verification required for share</Label>
                            </div>
                        </div>

                        {props.values.ageVerification ? (
                            <div className="space-y-2">
                                <Label htmlFor="verifiedAge">Age</Label>
                                <Field
                                    as={Input}
                                    id="verifiedAge"
                                    name="verifiedAge"
                                />
                            </div>
                        ) : null}

                        <h2 className="text-lg font-semibold tracking-tight pt-4">Media Display Settings</h2>

                        <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                                <Field
                                    id="galleryMediaFilter.enable"
                                    name="galleryMediaFilter.enable"
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-border"
                                />
                                <Label htmlFor="galleryMediaFilter.enable">Media type filters</Label>
                            </div>
                        </div>

                        {props.values.galleryMediaFilter.enable ? (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Field
                                            id="galleryMediaFilter.original"
                                            name="galleryMediaFilter.original"
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-border"
                                        />
                                        <Label htmlFor="galleryMediaFilter.original">Show originals</Label>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Field
                                            id="galleryMediaFilter.photos"
                                            name="galleryMediaFilter.photos"
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-border"
                                        />
                                        <Label htmlFor="galleryMediaFilter.photos">Show photos</Label>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Field
                                            id="galleryMediaFilter.videos"
                                            name="galleryMediaFilter.videos"
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-border"
                                        />
                                        <Label htmlFor="galleryMediaFilter.videos">Show videos</Label>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Field
                                            id="galleryMediaFilter.edited"
                                            name="galleryMediaFilter.edited"
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-border"
                                        />
                                        <Label htmlFor="galleryMediaFilter.edited">Show edited</Label>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Field
                                            id="galleryMediaFilter.prints"
                                            name="galleryMediaFilter.prints"
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-border"
                                        />
                                        <Label htmlFor="galleryMediaFilter.prints">Show prints</Label>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div className="flex items-center gap-3 pt-6 border-t border-border">
                            <Button type="submit">
                                Save Gallery
                            </Button>
                            <Button
                                type="button"
                                onClick={() => navigate(-1)}
                                variant="outline"
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}
